/* eslint-disable no-nested-ternary */
import * as React from 'react';
import Box from '@mui/material/Box';
import { Stack, useMediaQuery } from '@mui/material';
import { AppointmentType } from '@prisma/client';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import TabBar from '../components/filtering/TabBar';
import {
  Appointment,
  GeneralRequest,
  LoadedUser,
  ModalData,
  TabInt,
} from '../interfaces';
import ReuseableForm from '../components/form/ReuseableForm';
import SideBarModal from '../components/modal/SideBarModal';
import FullPageModal from '../components/modal/FullPageModal';
import {
  rejectFormValues,
  rejectValidationSchema,
  rejectInputGroups,
  RejectValues,
} from '../utils/form/rejectForm';
import { createCardList, createRows } from '../utils/table/requestsTable';
import { createTab } from '../utils/tabs/requestsTabs';
import { createModalData } from '../utils/modal/requestModal';
import {
  archiveAppointment,
  createAppointmentChat,
  invalidateAppointments,
  markDepositPaid,
  rejectAppointment,
  rejectGeneralRequest,
  sendAppointmentReminder,
} from '../services/appointments';
import Error from '../components/global/Error';
import ConfirmationModal from '../components/modal/ConfirmationModal';
import AppointmentForm from '../components/form/AppointmentForm';
import useUser from '../hooks/global/useUser';
import useSizeOptions from '../hooks/tattoos/useSizeOptions';
import useGetRequests from '../hooks/requests/useGetRequests';
import client from '../utils/react-query/client';
import AcceptRequestForm from '../components/form/AcceptRequestForm';
import useFeatureGuard from '../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../types/subscriptions';
import useSubscriptionModal from '../hooks/subscriptions/useSubscriptionModal';
import {
  isAppointmentDepositPaid,
  navigateToChat,
} from '../helpers/appointments';
import PageLayout from '../components/global/PageLayout';
import { createFooterAction } from '../helpers/global';
import useSort from '../hooks/global/useSort';
import { SORTABLE_KEYS } from '../constants/appointments';
import useGetUnreadRequests from '../hooks/requests/useGetUnreadRequests';
import useMarkRequestAsRead from '../hooks/requests/useMarkRequestAsRead';
import { ROUTES } from '../constants/global';
import useRequestsFilters from '../hooks/requests/useRequestsFilters';
import MultiSelectFilter from '../components/filtering/MultiSelectFilter';
import useGetAppointment from '../hooks/appointments/useGetAppointment';

type Tab = 'REQUESTED' | 'ACCEPTED' | 'REJECTED' | 'ARCHIVED';

function getTabValue(tab: Tab) {
  if (tab === 'REQUESTED') return 0;
  if (tab === 'ACCEPTED') return 1;
  if (tab === 'REJECTED') return 2;
  return 3;
}

export default function RequestsPage() {
  const { appointmentId } = useParams();
  const isLgScreen = useMediaQuery('(min-width:900px)');
  const navigate = useNavigate();

  const [currentTab, setCurrentTab] = React.useState<Tab>('REQUESTED');

  const [formError, setFormError] = React.useState('');

  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalData, setModalData] = React.useState<ModalData>();
  const [acceptModalOpen, setAcceptModalOpen] = React.useState(false);
  const [rejectModalOpen, setRejectModalOpen] = React.useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = React.useState(false);
  const [appointmentFormModalOpen, setAppointmentFormModalOpen] =
    React.useState(false);
  const [shouldScrollToTop, setShouldScrollToTop] = React.useState(false);

  const [selectedAppointment, setSelectedAppointment] = React.useState<
    Appointment | GeneralRequest
  >();

  const { artist } = useRequestsFilters();
  const { user: loadedUser } = useUser<LoadedUser>();

  const { sizeOptions } = useSizeOptions();
  const sort = useSort(SORTABLE_KEYS, {
    defaultSort: {
      orderBy: 'createdAt',
      order: 'desc',
    },
  });

  const { data: unreadData } = useGetUnreadRequests();
  const {
    appointments: unreadAppointments,
    generalRequests: unreadGeneralRequests,
  } = unreadData ?? {};
  const { mutate: markRequestAsRead } = useMarkRequestAsRead();

  const [createAptType, setCreateAptType] =
    React.useState<AppointmentType>('NEW_APPOINTMENT');

  const onCloseRequest = React.useCallback(() => {
    setModalOpen(false);
    setModalData(undefined);
    if (appointmentId) {
      navigate(ROUTES.requests, {
        replace: true,
        preventScrollReset: true,
      });
    }
  }, [appointmentId, navigate]);

  const {
    combinedAppointments: requestedAppointments,
    isLoading: isLoadingRequested,
    refetch: refetchRequestedAppts,
    isError: isApptError,
  } = useGetRequests({
    status: 'REQUESTED',
    enabled: currentTab === 'REQUESTED',
    sort,
    employeeIds: artist.state,
  });

  const {
    appointments: acceptedAppointments,
    isLoading: isLoadingAccepted,
    refetch: refetchAcceptedAppts,
    isError: isAcceptedApptError,
  } = useGetRequests({
    status: 'ACCEPTED',
    enabled: currentTab === 'ACCEPTED',
    sort,
    employeeIds: artist.state,
  });

  const {
    appointments: rejectedAppointments,
    isLoading: isLoadingRejected,
    refetch: refetchRejectedAppts,
    isError: isRejectedApptError,
  } = useGetRequests({
    status: 'REJECTED',
    enabled: currentTab === 'REJECTED',
    sort,
    employeeIds: artist.state,
  });

  const {
    combinedAppointments: archivedAppointments,
    isLoading: isLoadingArchived,
    refetch: refetchArchivedAppts,
    isError: isArchivedApptError,
  } = useGetRequests({
    status: 'ARCHIVED',
    enabled: currentTab === 'ARCHIVED',
    sort,
    employeeIds: artist.state,
  });

  const { openSubscriptionModal } = useSubscriptionModal();
  const canManageRequest = useFeatureGuard(Feature.MANAGE_REQUESTS);

  const closeAllModals = React.useCallback(() => {
    onCloseRequest();
    setAcceptModalOpen(false);
    setRejectModalOpen(false);
    setArchiveModalOpen(false);
    setAppointmentFormModalOpen(false);
  }, [onCloseRequest]);

  // Set up click action for each row of the requests table
  const openRequest = React.useCallback(
    (newModalData: ModalData, appointment: Appointment | GeneralRequest) => {
      if (!canManageRequest) {
        openSubscriptionModal();
        return;
      }
      if (
        'status' in appointment &&
        unreadAppointments?.includes(appointment.appointmentId)
      ) {
        markRequestAsRead({
          requestId: appointment.appointmentId,
          type: 'appointment',
        });
      } else if (
        'requestId' in appointment &&
        unreadGeneralRequests?.includes(appointment.requestId)
      ) {
        markRequestAsRead({
          requestId: appointment.requestId,
          type: 'generalRequest',
        });
      }
      setSelectedAppointment(appointment);
      setModalData(newModalData);
      setModalOpen(true);
    },
    [
      canManageRequest,
      markRequestAsRead,
      openSubscriptionModal,
      unreadAppointments,
      unreadGeneralRequests,
    ],
  );

  // Set up click actions for modal footer
  const acceptRequest = React.useCallback(() => {
    if (!canManageRequest) {
      openSubscriptionModal();
      return;
    }
    setAcceptModalOpen(true);
  }, [canManageRequest, openSubscriptionModal]);

  // Set up click actions for modal footer
  const openArchive = React.useCallback(() => {
    if (!canManageRequest) {
      openSubscriptionModal();
      return;
    }
    setArchiveModalOpen(true);
  }, [canManageRequest, openSubscriptionModal]);

  const rejectRequest = React.useCallback(() => {
    if (!canManageRequest) {
      openSubscriptionModal();
      return;
    }
    setRejectModalOpen(true);
  }, [canManageRequest, openSubscriptionModal]);

  const closeAppointmentForm = React.useCallback(() => {
    setAppointmentFormModalOpen(false);
  }, []);

  const openAppointmentForm = React.useCallback((type: AppointmentType) => {
    setCreateAptType(type);
    setAppointmentFormModalOpen(true);
  }, []);

  const smartRefresh = React.useCallback(
    async (
      appointment: Appointment | GeneralRequest,
      action?: 'accept' | 'reject' | 'archive',
    ) => {
      if (!appointment) return;
      if (!('status' in appointment)) {
        await refetchRequestedAppts();
      } else if (appointment.status === 'ACCEPTED') {
        await refetchAcceptedAppts();
      } else if (appointment.status === 'REQUESTED') {
        await refetchRequestedAppts();
      } else if (appointment.status === 'REJECTED') {
        await refetchRejectedAppts();
      } else if (appointment.status === 'ARCHIVED') {
        await refetchArchivedAppts();
      }

      // invalidate the accepted appointments query if the appointment was accepted
      if (action === 'accept') {
        await client.invalidateQueries({
          queryKey: ['requests', { status: 'ACCEPTED' }],
          exact: false,
        });
      } else if (action === 'reject') {
        await client.invalidateQueries({
          queryKey: ['requests', { status: 'REJECTED' }],
          exact: false,
        });
      } else if (action === 'archive') {
        await client.invalidateQueries({
          queryKey: ['requests', { status: 'ARCHIVED' }],
          exact: false,
        });
      }
    },
    [
      refetchAcceptedAppts,
      refetchArchivedAppts,
      refetchRejectedAppts,
      refetchRequestedAppts,
    ],
  );

  const resendBookingLink = React.useCallback(async () => {
    // If the appointment is not an appointment, return
    if (
      !selectedAppointment ||
      !('status' in selectedAppointment) ||
      selectedAppointment.status !== 'ACCEPTED' ||
      !loadedUser
    )
      return;
    try {
      await sendAppointmentReminder(selectedAppointment);
      toast.success('Booking link re-sent');
    } catch (e) {
      toast.error('Error re-sending booking link');
    }
  }, [loadedUser, selectedAppointment]);

  const markPaidCash = React.useCallback(async () => {
    if (
      !selectedAppointment ||
      !('status' in selectedAppointment) ||
      isAppointmentDepositPaid(selectedAppointment) ||
      selectedAppointment.status !== 'ACCEPTED'
    )
      return;
    try {
      await markDepositPaid(selectedAppointment?.appointmentId, {
        depositPaymentMethod: 'CASH',
      });
      await smartRefresh(selectedAppointment);
      toast.success('Deposit marked as paid');
      closeAllModals();
    } catch (e) {
      toast.error('Error marking deposit as paid');
    }
  }, [closeAllModals, selectedAppointment, smartRefresh]);

  const openChat = React.useCallback(async () => {
    if (selectedAppointment && loadedUser) {
      try {
        const chatResponse = await createAppointmentChat(
          selectedAppointment,
          loadedUser,
        );
        navigateToChat(navigate, chatResponse);
      } catch {
        toast.error('Error opening chat');
      }
    }
  }, [selectedAppointment, loadedUser, navigate]);

  const actions = React.useMemo(() => {
    const moreRequested = [
      createFooterAction('Archive', openArchive),
      createFooterAction('Schedule appointment', () =>
        openAppointmentForm('NEW_APPOINTMENT'),
      ),
      createFooterAction('Schedule consultation', () =>
        openAppointmentForm('CONSULTATION'),
      ),
    ];
    if (
      !!selectedAppointment &&
      'status' in selectedAppointment &&
      selectedAppointment.status === 'ACCEPTED'
    ) {
      moreRequested.push(
        createFooterAction('Resend booking link', resendBookingLink),
      );
      if (
        (selectedAppointment.depositPrice ?? 0) > 0 &&
        !isAppointmentDepositPaid(selectedAppointment)
      ) {
        moreRequested.push(
          createFooterAction('Mark deposit paid with cash', markPaidCash),
        );
      }
    }
    return {
      footer: [
        createFooterAction('Accept', acceptRequest),
        createFooterAction('Reject', rejectRequest),
        createFooterAction('Open Chat', openChat),
      ],
      acceptedFooter: [createFooterAction('Open Chat', openChat)],
      more: [createFooterAction('Archive', openArchive)],
      moreRequested,
      moreArchived: [
        createFooterAction('Schedule appointment', () =>
          openAppointmentForm('NEW_APPOINTMENT'),
        ),
        createFooterAction('Schedule consultation', () =>
          openAppointmentForm('CONSULTATION'),
        ),
      ],
    };
  }, [
    acceptRequest,
    markPaidCash,
    openAppointmentForm,
    openArchive,
    openChat,
    rejectRequest,
    resendBookingLink,
    selectedAppointment,
  ]);

  const archive = React.useCallback(
    async (appointment: Appointment | GeneralRequest | undefined) => {
      if (loadedUser && appointment) {
        try {
          await archiveAppointment(appointment);
          closeAllModals();

          await smartRefresh(appointment, 'archive');

          toast.success('Appointment archived');
        } catch {
          toast.error('Could not archive appointment');
        }
      }
    },
    [closeAllModals, loadedUser, smartRefresh],
  );

  const reject = React.useCallback(
    async (appointment: Appointment | GeneralRequest, values: RejectValues) => {
      if ((appointment as Appointment).appointmentId) {
        try {
          await rejectAppointment(appointment as Appointment, values);
          toast.success('Appointment Rejected');
          setFormError('');
          await smartRefresh(appointment, 'reject');
        } catch {
          setFormError(
            'Error updating appointment details. Please refresh the page and try again',
          );
          toast.error('Error rejecting appointment');
        } finally {
          closeAllModals();
        }
      } else {
        try {
          await rejectGeneralRequest(
            appointment as GeneralRequest,
            values,
            loadedUser?.userId || '',
          );
          toast.success('Appointment Rejected');
          setFormError('');
          await smartRefresh(appointment, 'reject');
        } catch {
          setFormError(
            'Error updating appointment details. Please refresh the page and try again',
          );
          toast.error('Error rejecting appointment');
        } finally {
          closeAllModals();
        }
      }
    },
    [closeAllModals, loadedUser?.userId, smartRefresh],
  );

  const navigateToCustomer = React.useCallback(
    (customerId: string) => {
      if (!canManageRequest) {
        openSubscriptionModal();
        return;
      }
      navigate(`/customers/${customerId}`);
    },
    [canManageRequest, navigate, openSubscriptionModal],
  );

  // Create tabs and tab content with each table
  const tabs = React.useMemo<TabInt[]>(() => {
    // Create sets of table rows/lists for each tab
    const requestedRows = createRows({
      items: requestedAppointments,
      status: 'REQUESTED',
      handleClick: openRequest,
      sizeOptions,
      navigateToCustomer,
      unreadRequests: unreadData,
      employeesToBusiness: loadedUser?.business?.employees,
      allBusinesses: loadedUser?.allBusinesses,
    });
    const acceptedRows = createRows({
      items: acceptedAppointments,
      status: 'ACCEPTED',
      handleClick: openRequest,
      sizeOptions,
      navigateToCustomer,
      unreadRequests: unreadData,
      allBusinesses: loadedUser?.allBusinesses,
    });
    const rejectedRows = createRows({
      items: rejectedAppointments,
      status: 'REJECTED',
      handleClick: openRequest,
      sizeOptions,
      navigateToCustomer,
      unreadRequests: unreadData,
      allBusinesses: loadedUser?.allBusinesses,
    });
    const archivedRows = createRows({
      items: archivedAppointments,
      status: 'ARCHIVED',
      handleClick: openRequest,
      sizeOptions,
      navigateToCustomer,
      allBusinesses: loadedUser?.allBusinesses,
    });
    const requestedCardList = createCardList({
      items: requestedAppointments,
      status: 'REQUESTED',
      handleClick: openRequest,
      loading: isLoadingRequested,
      sizeOptions,
      unreadRequests: unreadData,
    });
    const acceptedCardList = createCardList({
      items: acceptedAppointments,
      status: 'ACCEPTED',
      handleClick: openRequest,
      loading: isLoadingAccepted,
      sizeOptions,
      unreadRequests: unreadData,
    });
    const rejectedCardList = createCardList({
      items: rejectedAppointments,
      status: 'REJECTED',
      handleClick: openRequest,
      loading: isLoadingRejected,
      sizeOptions,
      unreadRequests: unreadData,
    });
    const archivedCardList = createCardList({
      items: archivedAppointments,
      status: 'ARCHIVED',
      handleClick: openRequest,
      loading: isLoadingArchived,
      sizeOptions,
    });
    return [
      createTab({
        label: 'New Requests',
        rows: isLgScreen ? requestedRows : undefined,
        list: isLgScreen ? undefined : requestedCardList,
        loading: isLoadingRequested,
        sort,
      }),
      createTab({
        label: 'Accepted Requests',
        rows: isLgScreen ? acceptedRows : undefined,
        list: isLgScreen ? undefined : acceptedCardList,
        loading: isLoadingAccepted,
        sort,
      }),
      createTab({
        label: 'Rejected Requests',
        rows: isLgScreen ? rejectedRows : undefined,
        list: isLgScreen ? undefined : rejectedCardList,
        loading: isLoadingRejected,
        sort,
      }),
      createTab({
        label: 'Archived Requests',
        rows: isLgScreen ? archivedRows : undefined,
        list: isLgScreen ? undefined : archivedCardList,
        loading: isLoadingArchived,
        sort,
      }),
    ];
  }, [
    acceptedAppointments,
    archivedAppointments,
    isLgScreen,
    isLoadingAccepted,
    isLoadingArchived,
    isLoadingRejected,
    isLoadingRequested,
    navigateToCustomer,
    openRequest,
    rejectedAppointments,
    requestedAppointments,
    sizeOptions,
    sort,
    loadedUser?.business?.employees,
    loadedUser?.allBusinesses,
    unreadData,
  ]);

  useGetAppointment(appointmentId!, {
    enabled:
      !!sizeOptions &&
      !!loadedUser?.allBusinesses &&
      !!loadedUser?.business?.employees,
    onSuccess: appt => {
      const newModalData = createModalData({
        appointment: appt,
        sizeOptions,
        navigateToCustomer,
        allBusinesses: loadedUser?.allBusinesses,
        employeesToBusiness: loadedUser?.business?.employees,
      });

      openRequest(newModalData, appt);
    },
    staleTime: 0,
    gcTime: 0,
  });

  const renderSideBarModal = React.useCallback(() => {
    if (!modalData || !selectedAppointment) return null;
    const appt = selectedAppointment as Appointment;
    const generalRequest = selectedAppointment as GeneralRequest;
    const isGeneralRequest = 'requestId' in generalRequest;
    const isArchived =
      appt.status === 'ARCHIVED' || !!generalRequest?.deletedAt;

    const isRequested =
      (isGeneralRequest && !generalRequest.deletedAt) ||
      appt.status === 'REQUESTED';
    const isAccepted = appt.status === 'ACCEPTED';
    const isRejected = appt.status === 'REJECTED';

    return (
      <SideBarModal
        modalOpen={modalOpen}
        setModalOpen={onCloseRequest}
        name={modalData.name}
        sections={modalData.sections}
        footerActions={
          isRequested
            ? actions.footer
            : isArchived || isAccepted || isRejected
            ? actions.acceptedFooter
            : []
        }
        moreActions={
          isRequested || isAccepted
            ? actions.moreRequested
            : isArchived
            ? actions.moreArchived
            : actions.more
        }
      />
    );
  }, [
    actions.acceptedFooter,
    actions.footer,
    actions.more,
    actions.moreArchived,
    actions.moreRequested,
    modalData,
    modalOpen,
    onCloseRequest,
    selectedAppointment,
  ]);

  const error =
    isApptError ||
    isRejectedApptError ||
    isAcceptedApptError ||
    isArchivedApptError
      ? 'Error loading requests'
      : '';

  const formModalName =
    createAptType === 'NEW_APPOINTMENT'
      ? 'Schedule appointment'
      : 'Schedule consultation';

  if (error !== '') {
    return (
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 200px)',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Error errorMessage={error} />
      </Box>
    );
  }

  return (
    <>
      <PageLayout title="Booking Requests">
        {artist.isVisible && (
          <Stack direction="row" alignItems="center" gap={1}>
            <MultiSelectFilter
              name={artist.name}
              options={artist.options}
              state={artist.state}
              setState={artist.setState}
              emptyLabel={artist.emptyLabel}
              allLabel={artist.allLabel}
              sx={{ m: 0 }}
            />
          </Stack>
        )}

        <TabBar
          tabs={tabs}
          currentTab={getTabValue(currentTab)}
          changeTab={tab => {
            setCurrentTab(
              tab === 0
                ? 'REQUESTED'
                : tab === 1
                ? 'ACCEPTED'
                : tab === 2
                ? 'REJECTED'
                : 'ARCHIVED',
            );
          }}
        />

        {renderSideBarModal()}
      </PageLayout>
      <FullPageModal
        modalOpen={acceptModalOpen}
        setModalOpen={setAcceptModalOpen}
        name="Accept Request">
        <AcceptRequestForm
          appointment={selectedAppointment}
          closeModals={closeAllModals}
          refreshData={smartRefresh}
          formError={formError}
          setFormError={setFormError}
        />
      </FullPageModal>
      <FullPageModal
        modalOpen={rejectModalOpen}
        setModalOpen={setRejectModalOpen}
        name="Reject Request">
        <ReuseableForm
          initFormValues={rejectFormValues}
          validationSchema={rejectValidationSchema}
          inputGroups={rejectInputGroups}
          selectedAppointment={selectedAppointment}
          submitAction={reject}
          formError={formError}
        />
      </FullPageModal>

      <FullPageModal
        modalOpen={appointmentFormModalOpen}
        setModalOpen={setAppointmentFormModalOpen}
        handleClose={closeAppointmentForm}
        shouldScrollTop={shouldScrollToTop}
        setShouldScrollToTop={setShouldScrollToTop}
        name={formModalName}>
        <AppointmentForm
          selectedRequest={selectedAppointment}
          onClose={() => setAppointmentFormModalOpen(false)}
          onCreateUpdateSuccess={async params => {
            if (selectedAppointment) await smartRefresh(selectedAppointment);
            await invalidateAppointments();

            const currentAptId =
              selectedAppointment && 'appointmentId' in selectedAppointment
                ? selectedAppointment.appointmentId
                : '';
            const { newAptId } = params;

            const apptId = currentAptId || newAptId;
            if (apptId) {
              navigate(`${ROUTES.calendar}/${apptId}`);
            }
          }}
          setShouldScrollToTop={setShouldScrollToTop}
          appointmentType={createAptType}
        />
      </FullPageModal>

      <ConfirmationModal
        open={archiveModalOpen}
        handleClose={() => setArchiveModalOpen(false)}
        title="Archive Request"
        message={
          selectedAppointment &&
          (selectedAppointment as Appointment).appointmentId &&
          (selectedAppointment as Appointment).status === 'ACCEPTED'
            ? 'This action cannot be undone. Are you sure you want to archive this request? Archiving will still allow customers to schedule this appointment. If you no longer want them to, reject the request instead.'
            : 'This action cannot be undone. Are you sure you want to archive this request? You will no longer be able to view the details of this request once you archive.'
        }
        negativeAction={() => setArchiveModalOpen(false)}
        positiveAction={() => archive(selectedAppointment)}
        negativeText=""
        positiveText="Archive"
      />
    </>
  );
}
